import type { IPostComment } from '~/types/community';

export type CommentsSortBy = 'rating' | 'date';
export type CommentsSortOrder = 'asc' | 'desc';

const THREAD_MAX_DEPTH = 3;

const sortByRatingDesc = (a: IPostComment, b: IPostComment) => b.rating - a.rating;

const sortByDateAsc = (a: IPostComment, b: IPostComment) =>
  new Date(a.created_at).getTime() - new Date(b.created_at).getTime();

const sortByDateDesc = (a: IPostComment, b: IPostComment) =>
  new Date(b.created_at).getTime() - new Date(a.created_at).getTime();

export function createCommentTree(commentDictionary: Record<number, IPostComment>): IPostComment[] {
  function predicate(comment: IPostComment, index: number, array: IPostComment[]) {
    return !comment.is_deleted || array.some(c => c.parent_id === comment.id);
  }

  let comments = Object.values(commentDictionary);

  for (let i = 0; i < THREAD_MAX_DEPTH; i++) {
    const previousCommentsLength = comments.length;

    comments = comments.filter(predicate);

    if (comments.length === previousCommentsLength) break;
  }

  const hashTable = comments.reduce<Record<number, IPostComment>>((hash, comment) => {
    comment.replies = [];
    hash[comment.id] = comment;

    return hash;
  }, {});

  return comments.reduce<IPostComment[]>((tree, comment) => {
    if (comment.parent_id) hashTable[comment.parent_id].replies!.push(hashTable[comment.id]);
    else tree.push(hashTable[comment.id]);

    return tree;
  }, []);
}

export function sortCommentTree(
  comments: IPostComment[],
  sortBy: CommentsSortBy = 'rating',
  sortOrder: CommentsSortOrder = 'desc',
  level = 0,
): void {
  let sortFunc;

  if (sortBy === 'rating') {
    // sort first 2 level of comments by rating DESC, sort 3rd level by date ASC
    if (level < 2) sortFunc = sortByRatingDesc;
    else sortFunc = sortByDateAsc;
  } else if (sortBy === 'date') {
    if (sortOrder === 'asc') sortFunc = sortByDateAsc;
    else sortFunc = sortByDateDesc;
  }

  comments.sort(sortFunc);

  comments.forEach(comment => {
    if (comment.replies && comment.replies.length) sortCommentTree(comment.replies, sortBy, sortOrder, level + 1);
  });
}

export function* walkCommentTree(comments: IPostComment[]): Generator<IPostComment> {
  for (const comment of comments) {
    yield comment;
    if (comment.replies && comment.replies.length) yield* walkCommentTree(comment.replies);
  }
}

export function searchCommentTreeById(commentTree: IPostComment[], commentId: number): IPostComment | null {
  for (const comment of walkCommentTree(commentTree)) {
    if (comment.id === commentId) return comment;
  }
  return null;
}

export function countCommentInTree(comment: IPostComment): number {
  if (!comment.replies) {
    return 1;
  }

  return comment.replies.reduce((count: number, comment: IPostComment) => count + countCommentInTree(comment), 1);
}

export function isThreadSafeToDelete(comment: IPostComment): boolean {
  return comment.replies!.some(reply => !reply.is_deleted) ? false : comment.replies!.every(isThreadSafeToDelete);
}
